import type React from 'react';

/**
 * SearchBarPlaceholder Component
 *
 * Purpose:
 * This component provides a custom animated placeholder for the search input field
 * that is ONLY visible on mobile devices. It addresses a common UX problem on mobile:
 * long placeholder text gets truncated and users can't read the full message.
 *
 * How it works:
 * - On desktop (md breakpoint and up): Uses the native HTML input placeholder attribute
 * - On mobile: Renders an absolutely positioned div that scrolls horizontally to reveal
 *   the full placeholder text, then loops back to the start
 *
 * Why it's needed:
 * - Mobile screens have limited width, and placeholders like "Ask us anything about [Site Name]"
 *   would be cut off
 * - The animation lets users read the complete message without truncation
 * - The scrolling effect is subtle and non-intrusive (8s loop with pauses at start/end)
 *
 * Implementation details:
 * - Hidden when: input is focused OR user has typed something (message.length > 0)
 * - Animation: Scrolls from 0% to ~100% of text width over 8 seconds
 * - Styling matches the native placeholder: same font, size, and theme placeholder tokens
 * - Uses pointer-events-none to ensure it doesn't interfere with input interaction
 */

interface SearchBarPlaceholderProps {
    /** The placeholder text to display and animate */
    placeholder: string;
}

const SearchBarPlaceholder: React.FC<SearchBarPlaceholderProps> = ({ placeholder }) => {
    return (
        <>
            {/* Custom Scrolling Placeholder - Only visible on mobile screens (< md breakpoint) */}
            <div
                className="md:hidden absolute top-0 left-0 pointer-events-none overflow-hidden whitespace-nowrap w-full"
                style={{
                    color: 'var(--rw-text-placeholder)',
                    fontSize: 'var(--rw-font-size-body)',
                    fontWeight: 'var(--rw-font-weight-placeholder)',
                    letterSpacing: 'normal',
                    lineHeight: 'var(--rw-line-height-input)',
                }}
            >
                <div
                    className="animate-scroll-placeholder"
                    style={{
                        animation: 'scrollPlaceholder 8s linear infinite',
                    }}
                >
                    {placeholder}
                </div>
            </div>

            {/* Keyframe animation for horizontal scrolling effect */}
            <style>{`
                @keyframes scrollPlaceholder {
                    /* Stay at start position for 20% of animation (1.6s) */
                    0%, 20% {
                        transform: translateX(0);
                    }
                    /* Move to end and stay there for 20% of animation (1.6s) */
                    80%, 100% {
                        transform: translateX(calc(-100% + 100px));
                    }
                    /* Middle 60% (4.8s) is smooth scrolling from start to end */
                }
            `}</style>
        </>
    );
};

export default SearchBarPlaceholder;
